import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { AXIOS_DEFAULT_CONFIG, createBearerAuthHeaders } from '../common';
import {
  GetPremiumStatusRawResult,
  GetPremiumStatusResult,
  IImportJWTTokenResult,
  IPrivateEndpoint,
  IProvider,
  IWorkerEndpoint,
  MethodsConfig,
  RateLimitConfig,
  RestrictedDomains,
  RestrictedIps,
} from './types';
import {
  ICustomerRequestsMRPCRequestBodyParams,
  ICustomerRequestsMRPCRequestQueryParams,
  ICustomerRequestsMRPCResponse,
  IUsageDataMRPCRequestBodyParams,
  IUsageDataMRPCRequestQueryParams,
  IUsageDataMRPCResponse,
} from './telemetry/types';

export class WorkerGateway {
  public api: AxiosInstance;

  constructor(private readonly config: AxiosRequestConfig) {
    this.api = axios.create({ ...config, ...AXIOS_DEFAULT_CONFIG });
  }

  public addJwtToken(signedToken: string) {
    this.api = axios.create({
      ...this.config,
      ...AXIOS_DEFAULT_CONFIG,
      headers: {
        Authorization: `Bearer ${signedToken}`,
      },
    });
  }

  public removeJwtToken() {
    this.api = axios.create({ ...this.config, ...AXIOS_DEFAULT_CONFIG });
  }

  async importJwtToken(jwtToken?: string): Promise<IImportJWTTokenResult> {
    const { data } = await this.api.post('/api/v1/jwt', {
      jwtToken,
      createNew: 'yes',
    });

    return data;
  }

  async getProvider(): Promise<IProvider> {
    const { data } = await this.api.get<IProvider>('/api/v1/provider');

    return data;
  }

  async getEndpoints(): Promise<IWorkerEndpoint[]> {
    const { data } = await this.api.get<IWorkerEndpoint[]>(
      '/api/v1/private/node',
    );

    return data;
  }

  async addPrivateEndpoint(
    endpoint: IPrivateEndpoint,
  ): Promise<IWorkerEndpoint> {
    const { data } = await this.api.post('/api/v1/private/node', endpoint);

    return data;
  }

  async editPrivateEndpoint(
    endpoint: IPrivateEndpoint,
  ): Promise<IWorkerEndpoint> {
    const { data } = await this.api.post('/api/v1/private/node', endpoint);

    return data;
  }

  async deletePrivateEndpoint(id: string): Promise<void> {
    await this.api.delete<IPrivateEndpoint>('/api/v1/private/node', {
      params: { id },
    });
  }

  async getChainRestrictedDomains(chainId: string): Promise<RestrictedDomains> {
    const { data } = await this.api.get(
      `/api/v1/user/whitelist/${chainId}/referer`,
    );

    return data;
  }

  async getChainRestrictedIps(chainId: string): Promise<RestrictedIps> {
    const { data } = await this.api.get(`/api/v1/user/whitelist/${chainId}/ip`);

    return data;
  }

  async editChainRestrictedDomains(
    chainId: string,
    domains: RestrictedDomains,
  ): Promise<RestrictedDomains> {
    const { data } = await this.api.post(
      `/api/v1/user/whitelist/${chainId}/referer`,
      domains,
    );

    return data;
  }

  async editChainRestrictedIps(
    chainId: string,
    ips: RestrictedDomains,
  ): Promise<RestrictedIps> {
    const { data } = await this.api.post(
      `/api/v1/user/whitelist/${chainId}/ip`,
      ips,
    );

    return data;
  }

  // The worker token is passed as a per-request Authorization header so a call
  // can be scoped to a specific project's key without mutating the shared axios
  // instance (which would affect other worker requests).
  async getUserMethods(workerToken?: string): Promise<MethodsConfig> {
    const { data } = await this.api.get<MethodsConfig>('/api/v1/user/methods', {
      headers: createBearerAuthHeaders(workerToken),
    });

    return data;
  }

  async updateUserMethods(
    config: MethodsConfig,
    workerToken?: string,
  ): Promise<MethodsConfig> {
    const { data } = await this.api.post<MethodsConfig>(
      '/api/v1/user/methods',
      config,
      { headers: createBearerAuthHeaders(workerToken) },
    );

    return data;
  }

  async getUserRateLimit(workerToken?: string): Promise<RateLimitConfig> {
    const { data } = await this.api.get<RateLimitConfig>(
      '/api/v1/user/ratelimit',
      { headers: createBearerAuthHeaders(workerToken) },
    );

    return data;
  }

  async updateUserRateLimit(
    config: RateLimitConfig,
    workerToken?: string,
  ): Promise<RateLimitConfig> {
    const { data } = await this.api.post<RateLimitConfig>(
      '/api/v1/user/ratelimit',
      config,
      { headers: createBearerAuthHeaders(workerToken) },
    );

    return data;
  }

  async getPremiumStatus(
    userEndpointToken: string,
  ): Promise<GetPremiumStatusResult> {
    const {
      data: { freemium: isFreemium, status, isEnterprise },
    } = await this.api.get<GetPremiumStatusRawResult>(
      `/api/v1/user/status/${userEndpointToken}`,
    );

    return { isFreemium, status, isEnterprise };
  }

  async getSharkTelemetry(
    body: IUsageDataMRPCRequestBodyParams,
    params: IUsageDataMRPCRequestQueryParams,
  ): Promise<IUsageDataMRPCResponse> {
    const { data: response } = await this.api.post<IUsageDataMRPCResponse>(
      '/api/v2/telemetry/usage-data',
      body,
      { params },
    );

    return response;
  }

  async getRawRequestsTelemetry(
    body: ICustomerRequestsMRPCRequestBodyParams,
    params?: ICustomerRequestsMRPCRequestQueryParams,
  ): Promise<ICustomerRequestsMRPCResponse> {
    const { data: response } =
      await this.api.post<ICustomerRequestsMRPCResponse>(
        '/api/v2/telemetry/requests',
        body,
        { params },
      );

    return response;
  }
}
